import { VoidCallback1 } from '@deqo/foundation';
import React from 'react';
import styled, { css } from 'styled-components';
import { Colors } from '.';
import { LabelValue } from '../platform/types/LabelValue';

export type ButtonToggleSize = 'sm' | 'md' | 'lg';
export type ButtonToggleVariant = 'default' | 'fancy';

const Option = styled.div<{ active: boolean, color?: string }>`
    color: ${Colors.textLight};    
    cursor: pointer;
    text-align: center;
    user-select: none;

    &:hover {
        background: ${Colors.hover};
    }
    &:active {
        background: ${Colors.active};
    }

    ${p => p.active ? css`

        background: ${p.color || Colors.primary} !important;
        color: ${Colors.contrastTextColorFor(p.color || Colors.primary)};

    ` : ''}

    /* transition: all 100ms linear; */
`;

const Track = styled.div<{ size: ButtonToggleSize, color?: string }>`
    display: inline-flex;
    flex-direction: row;
    flex: 0 0 auto;
    gap: .125rem;
    padding: .125rem;
    border: ${p => p.color ? `solid 0.125rem ${p.color}` : `solid 0.0625rem ${Colors.border}`};
    border-radius: ${p => p.size === 'lg' ? '.5rem' : '.375rem'};
    background: white;

    ${Option} {
        ${p => p.size === 'sm' && css`
            padding: .125rem .25rem;
            border-radius: .25rem;
            font-size: 0.75rem;
        `};

        ${p => p.size === 'md' && css`
            padding: .25rem .5rem;
            border-radius: .25rem;
            font-size: 0.875rem;
        `};

        ${p => p.size === 'lg' && css`
            padding: .5rem .75rem;
            border-radius: .375rem;
            font-size: 1rem;
        `};
    }
`;

export interface ButtonToggleOption<T> extends LabelValue<T> {
    color?: string;
}

export type ButtonToggleProps<T> = {
    size?: ButtonToggleSize,
    variant?: ButtonToggleVariant,
    options: ButtonToggleOption<T>[],
    selected: T,
    onChange: VoidCallback1<T>,
};

const Base = <T,>({ size, variant, options, selected, onChange }: ButtonToggleProps<T>) => {
    size = size || 'lg';
    variant = variant || 'default';

    const activeOption = options.find(o => o.value === selected);

    return <Track size={size} color={variant === 'fancy' ? activeOption?.color : undefined}>
        {options.map((o, i) =>
            <Option key={o.label} active={o.value === selected} color={o.color}
                onClick={() => onChange(o.value)}>{o.label}</Option>
        )}
    </Track>;

};

export const ButtonToggle = Object.assign(Base, { Option, Track });
